import { computed, inject, Injectable, signal, WritableSignal } from '@angular/core';
import { Subject } from 'rxjs';
import { IUser } from '@lb/api/mylb/interfaces/iuser.interface';
import { IUserContextAssociatedUser } from '@lb/api/mylb/interfaces/iuser-context-associated-user.interface';
import { AccountStateService } from '@lb/state/account-state.service';

export type UserSwitchMode = 'none' | 'guardian' | 'institution';

/**
 * UserContextService — the single authoritative signal for "whose data am I currently viewing."
 *
 * This service bridges the logged-in identity (AccountStateService) with an optional
 * "viewing-as" context (guardian viewing a child, institution contact viewing a learner).
 *
 * Downstream state (lessons, enrollment) should migrate to consume `effectiveUserID` over time.
 * See also: LearnerUserStateService (learner app) — currently a parallel source of truth;
 * migration to effectiveUserID is tracked as a follow-on task.
 */
@Injectable({ providedIn: 'root' })
export class UserContextService {
	private readonly _accountStateService = inject(AccountStateService);

	/** Whether a user-switching UI should be shown and which variant ('guardian' for small lists, 'institution' for searchable large lists). */
	public readonly switchMode: WritableSignal<UserSwitchMode> = signal<UserSwitchMode>('none');

	/** The full list of users the logged-in user can switch to (populated by the host app on init). */
	public readonly associatedUsers: WritableSignal<IUserContextAssociatedUser[]> = signal<IUserContextAssociatedUser[]>([]);

	/**
	 * The userID currently being viewed.
	 * `undefined` means the logged-in user is viewing their own data.
	 * When set to another user's ID, the header, lesson state, and enrollment state
	 * should reflect that user.
	 */
	public readonly viewingUserID: WritableSignal<number | undefined> = signal<number | undefined>(undefined);

	/**
	 * The full IUser object for the currently viewed user.
	 * Populated by the host app after fetching the user's data.
	 * Used for display in the menu header ("Viewing as …").
	 */
	public readonly viewingUser: WritableSignal<IUser | undefined> = signal<IUser | undefined>(undefined);

	/** True when the logged-in user is viewing someone else's data. */
	public readonly isViewingOtherUser = computed(
		() => this.viewingUserID() !== undefined && this.viewingUserID() !== this._accountStateService.user()?.userID
	);

	/**
	 * Always resolves to a valid userID — either the viewed user or the logged-in user.
	 * This is the intended long-term replacement for `LearnerUserStateService.activeLearnerUserID`
	 * as downstream state (lessons, enrollment) migrates to this service.
	 * TODO (follow-on): migrate lesson-state and enrollment-state to consume effectiveUserID.
	 */
	public readonly effectiveUserID = computed(() => this.viewingUserID() ?? this._accountStateService.user()?.userID);

	/**
	 * Emits the userID requested when the user selects a different account to view.
	 * The host app subscribes to this to trigger its own data reload (e.g. loadActiveLearnerUser).
	 */
	public readonly switchUserRequested = new Subject<number>();

	/**
	 * Initialises the switch context for guardian or institution users.
	 * Call this from the host app once the associated user list is loaded.
	 *
	 * @param mode 'guardian' for a small list; 'institution' for a searchable large list
	 * @param users The list of users this account can switch to
	 * @param initialViewingUserID The userID to treat as the initial active user (optional)
	 */
	public setSwitchContext(mode: UserSwitchMode, users: IUserContextAssociatedUser[], initialViewingUserID?: number): void {
		this.switchMode.set(mode);
		this.associatedUsers.set(users);
		this.viewingUserID.set(initialViewingUserID);
	}

	/**
	 * Switch the active viewed user. Notifies subscribers via `switchUserRequested`.
	 * The host app is responsible for fetching the new user's data and calling `viewingUser.set(...)`.
	 */
	public requestSwitchUser(userID: number): void {
		this.viewingUserID.set(userID);
		this.switchUserRequested.next(userID);
	}

	/** Resets all context to defaults (no switching UI, own data). */
	public clearSwitchContext(): void {
		this.switchMode.set('none');
		this.associatedUsers.set([]);
		this.viewingUserID.set(undefined);
		this.viewingUser.set(undefined);
	}
}
